<script setup>
import { ref, computed } from 'vue';
import { useField } from 'vee-validate';
import { useRouter } from 'vue-router';
import api from '@/services/axios';
import { useToast } from 'primevue/usetoast';
import { useFormValidation } from '@/composables/useFormValidation';
import { registerSchema } from '@/formValidationschemas';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import PasswordInput from '@/components/general/PasswordInput.vue';
import { USER_ROLES } from '@/types';
import { ROUTES } from '@/router/routeNames';
import { useRoute } from 'vue-router';
import { businessCategories } from '@/constants';
import mixpanel from '@/services/mixpanel';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const processing = ref(false);
const serverErrors = ref({});
const isDeliveryDriver = computed(() => route.query.isDeliveryDriver === 'true');
const isBusiness = computed(() => route.query.isBusiness === 'true');
const role = computed(() => {
  if(isDeliveryDriver.value) return USER_ROLES.DRIVER;
  if(isBusiness.value) return USER_ROLES.BUSINESS;
  return USER_ROLES.CUSTOMER
})
// Initialize form with vee-validate
const initialValues = {
  name: '',
  email: '',
  phone: '',
  address: '',
  password: '',
  confirmPassword: '',
  // role: isDeliveryDriver.value ? USER_ROLES.DRIVER : USER_ROLES.CUSTOMER,
  business_category: '',
  other_category: '',
  terms: false,
};

const { handleSubmit, errors, resetForm } = useFormValidation(
  registerSchema,
  initialValues
);

// Setup form fields with useField
const { value: name } = useField('name');
const { value: email } = useField('email');
const { value: phone } = useField('phone');
const { value: address } = useField('address');
const { value: password } = useField('password');
const { value: confirmPassword } = useField('confirmPassword');
const { value: business_category } = useField('business_category');
const { value: other_category } = useField('other_category');
const { value: terms } = useField('terms');


watch(email, () => {
  serverErrors.value.email = [];
})

const submit = handleSubmit(async (values) => {
  processing.value = true;
  serverErrors.value = {};

  try {
    // Transform business category if needed
    const formData = {
      ...values,
      role: role.value,
      business_category: values.business_category === 'Other' ? values.other_category : values.business_category,
      password_confirmation: values.confirmPassword
    };

    await api.post('/register', formData);
    mixpanel.track('User Registered', formData);

    toast.add({
      severity: 'success',
      summary: 'Registration Successful',
      detail: 'Your account has been created successfully',
      life: 3000
    });
    // Reset form after successful submission
    resetForm();

    // Redirect to login page
    router.push({ name: ROUTES.LOGIN });
  } catch (error) {
    if (error.response && error.response.data && error.response.data.errors) {
      // Store backend validation errors
      serverErrors.value = error.response.data.errors;

      toast.add({
        severity: 'error',
        summary: 'Registration Failed',
        detail: 'Please check the form for errors',
        life: 3000
      });
    } else {
      // Generic error handling
      console.error('Registration error:', error);
      toast.add({
        severity: 'error',
        summary: 'Registration Failed',
        detail: 'An unexpected error occurred',
        life: 3000
      });
    }
  } finally {
    processing.value = false;
  }
});

const roles = computed(() => {
  if(isBusiness.value) return  [
    {
      id: USER_ROLES.BUSINESS,
      title: 'Business',
      description: 'Sell your products to local customers',
      icon: `<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>`
    }

  ]

  return [

    {
      id: USER_ROLES.CUSTOMER,
      title: 'Customer',
      description: 'Order groceries and food from local businesses',
      icon: `<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>`
    },
  ]
})

</script>

<template>
  <div class="flex flex-col justify-center items-center px-4">
    <div v-if="isDeliveryDriver">
    </div>
    <!-- Logo/Brand -->
    <RouterLink :to="{ name: ROUTES.HOME }" class="mb-8">
      <h1 class="text-4xl font-black text-green-600">BoxBuddy</h1>
    </RouterLink>

    <!-- Registration Card -->
    <div class="p-8 w-full max-w-md bg-white rounded-2xl shadow-lg">
      <h2 class="mb-6 text-2xl font-bold text-gray-900">Create your account</h2>

      <form @submit.prevent="submit">
        <div class="space-y-6">
          <!-- Role Selection -->
          <div v-if="!isDeliveryDriver" class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">I want to...</label>
            <div class="grid grid-cols-1 gap-4">
              <div
                v-for="roleOption in roles"
                :key="roleOption.id"
                @click="role = roleOption.id"
                class="flex flex-col items-center p-4 rounded-lg border transition-colors cursor-pointer"
                :class="{
                  'border-green-500 bg-green-50': role === roleOption.id,
                  'border-gray-200 hover:border-green-300': role !== roleOption.id
                }">
                <div class="mb-2 text-green-600" v-html="roleOption.icon"></div>
                <h3 class="text-sm font-medium">{{ roleOption.title }}</h3>
                <p class="mt-1 text-xs text-center text-gray-500">{{ roleOption.description }}</p>
              </div>
            </div>
            <!-- <span v-if="errors.role || (serverErrors.role ? serverErrors.role[0] : '')" class="text-sm text-red-600">{{
              errors.role || (serverErrors.role ? serverErrors.role[0] : '') }}</span> -->
          </div>

          <!-- Service Fee Information (Only shown for business) -->
          <!-- <transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 transform -translate-y-2"
            enter-to-class="opacity-100 transform translate-y-0"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100 transform translate-y-0"
            leave-to-class="opacity-0 transform -translate-y-2">
            <div
              v-if="role === USER_ROLES.BUSINESS"
              class="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 sm:p-5">
              <div class="flex flex-col gap-2 items-start sm:gap-3">
                <div
                  class="flex flex-shrink-0 justify-center items-center mt-0.5 w-8 h-8 bg-blue-100 rounded-full sm:w-10 sm:h-10">
                  <svg class="w-4 h-4 text-blue-600 sm:w-5 sm:h-5" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="mb-2 text-sm font-semibold text-gray-900 sm:text-base">Business Service Fee</h4>
                  <p class="mb-3 text-xs leading-relaxed text-gray-700 sm:text-sm">
                    As a business partner, we charge a <span class="font-semibold text-blue-700">10% service fee</span>
                    on each order to maintain and improve our platform.
                    The maximum fee per order is capped at <span
                      class="font-semibold text-blue-700 whitespace-nowrap">₦1,500</span>.
                  </p>
                  <div class="p-2.5 text-xs rounded-lg bg-white/80 sm:p-3 sm:text-sm">
                    <div class="mb-2 font-medium text-gray-900">Examples:</div>
                    <div class="space-y-1.5 text-gray-600">
                      <div class="flex gap-2 justify-between items-center">
                        <span class="whitespace-nowrap">Order: ₦5,000</span>
                        <span class="font-medium text-right text-gray-900">Fee: ₦500 <span
                            class="hidden xs:inline">(10%)</span></span>
                      </div>
                      <div class="flex gap-2 justify-between items-center">
                        <span class="whitespace-nowrap">Order: ₦15,000</span>
                        <span class="font-medium text-right text-gray-900">Fee: ₦1,500 <span
                            class="hidden xs:inline">(10% capped)</span></span>
                      </div>
                      <div class="flex gap-2 justify-between items-center">
                        <span class="whitespace-nowrap">Order: ₦20,000</span>
                        <span class="font-medium text-right text-gray-900">Fee: ₦1,500 <span
                            class="hidden xs:inline">(capped max)</span></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition> -->

          <!-- Business Category Selection (only shown for business role) -->
          <div v-if="role === USER_ROLES.BUSINESS" class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">Business Category</label>
            <select
              v-model="business_category"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              required>
              <option value="" disabled>Select a category</option>
              <option v-for="category in businessCategories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
            <span
              v-if="errors.business_category || (serverErrors.business_category ? serverErrors.business_category[0] : '')"
              class="text-sm text-red-600">{{ errors.business_category || (serverErrors.business_category ?
              serverErrors.business_category[0] : '') }}</span>
          </div>
          <div v-if="business_category === 'Other'">
            <label for="other_category" class="block text-sm font-medium text-gray-700">Other Category</label>
            <input
              id="other_category"
              v-model="other_category"
              type="text"
              :required="business_category === 'Other'"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            <span v-if="errors.other_category || (serverErrors.other_category ? serverErrors.other_category[0] : '')"
              class="text-sm text-red-600">{{ errors.other_category || (serverErrors.other_category ?
              serverErrors.other_category[0] : '') }}</span>
          </div>

          <!-- Name -->
          <div>
            <label for="name" class="block text-sm font-medium text-gray-700">
              {{ role === USER_ROLES.BUSINESS ? 'Business' : 'Full' }} Name
            </label>
            <input
              id="name"
              v-model="name"
              type="text"
              required
              autocomplete="name"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            <span v-if="errors.name || (serverErrors.name ? serverErrors.name[0] : '')" class="text-sm text-red-600">{{
              errors.name || (serverErrors.name ? serverErrors.name[0] : '') }}</span>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            <span v-if="errors.email || (serverErrors.email ? serverErrors.email[0] : '')"
              class="text-sm text-red-600">{{ errors.email || (serverErrors.email ? serverErrors.email[0] : '')
              }}</span>
          </div>

          <!-- Phone -->
          <div>
            <label for="phone" class="block text-sm font-medium text-gray-700">Phone Number</label>
            <input
              id="phone"
              v-model="phone"
              type="tel"
              required
              autocomplete="tel"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            <span v-if="errors.phone || (serverErrors.phone ? serverErrors.phone[0] : '')"
              class="text-sm text-red-600">{{ errors.phone || (serverErrors.phone ? serverErrors.phone[0] : '')
              }}</span>
          </div>

          <!-- Address -->
          <div>
            <label for="address" class="block text-sm font-medium text-gray-700">Address</label>
            <textarea
              id="address"
              v-model="address"
              type="text"
              required
              rows="4"
              placeholder="Enter your full address"
              autocomplete="street-address"
              class="block px-4 py-3 w-full text-gray-900 rounded-xl border-0 ring-1 ring-inset ring-gray-300 shadow-sm placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            <span v-if="errors.address || (serverErrors.address ? serverErrors.address[0] : '')"
              class="text-sm text-red-600">{{ errors.address || (serverErrors.address ? serverErrors.address[0] : '')
              }}</span>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
            <PasswordInput
              id="password"
              v-model="password"
              required
              autocomplete="new-password"
              placeholder="Enter your password" />
            <span v-if="errors.password || (serverErrors.password ? serverErrors.password[0] : '')"
              class="text-sm text-red-600">{{ errors.password || (serverErrors.password ? serverErrors.password[0] : '')
              }}</span>
          </div>

          <!-- Confirm Password -->
          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <PasswordInput
              id="confirmPassword"
              v-model="confirmPassword"
              required
              autocomplete="new-password"
              placeholder="Confirm your password" />
            <span
              v-if="errors.confirmPassword || (serverErrors.password_confirmation ? serverErrors.password_confirmation[0] : '')"
              class="text-sm text-red-600">{{ errors.confirmPassword || (serverErrors.password_confirmation ?
              serverErrors.password_confirmation[0] : '') }}</span>
          </div>

          <!-- Terms -->
          <div>
            <label class="flex items-center">
              <input
                v-model="terms"
                type="checkbox"
                required
                class="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500" />
              <span class="ml-2 text-sm text-gray-600">
                I agree to the
                <RouterLink :to="{ name: ROUTES.TERMS_AND_CONDITIONS }"
                  class="font-medium text-green-600 hover:text-green-500">Terms</RouterLink>
                and
                <RouterLink :to="{ name: ROUTES.PRIVACY_POLICY }"
                  class="font-medium text-green-600 hover:text-green-500">Privacy Policy</RouterLink>
              </span>
            </label>
            <span v-if="errors.terms || (serverErrors.terms ? serverErrors.terms[0] : '')"
              class="text-sm text-red-600">{{ errors.terms || (serverErrors.terms ? serverErrors.terms[0] : '')
              }}</span>
          </div>

          <div>
            <PrimaryButton
              type="submit"
              class="justify-center w-full"
              :disabled="processing">
              <span v-if="processing" class="mr-2">
                <i class="pi pi-spin pi-spinner"></i>
              </span>
              Create Account
            </PrimaryButton>
          </div>
        </div>
      </form>

      <div class="mt-6 text-center">
        <span class="text-sm text-gray-600">Already have an account?</span>
        <RouterLink
          :to="{ name: ROUTES.LOGIN }"
          class="ml-1 text-sm font-medium text-green-600 hover:text-green-500">
          Log in
        </RouterLink>
      </div>
    </div>
  </div>
</template>
